import React, { useState, useEffect } from 'react';
import DecorationServices from './DecorationServices';
import DateTimeSelector from './DateTimeSelector';
import EquipmentRental from './EquipmentRental';
import BookingSummary from './BookingSummary';
import MainFunction from './main_function';
import { fetchBookedDates } from '../../services/BookingService';
import './BookingSystem.css';

const BookingSystem = () => {
    const [selectedEventPackage, setSelectedEventPackage] = useState({
        id: null,
        name: '',
        price: 0
    });

    const [selectedDecorations, setSelectedDecorations] = useState([]);

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [bookedDates, setBookedDates] = useState([]);

    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting] = useState(false);
    const [customerInfo, setCustomerInfo] = useState({
        name: '',
        email: '',
        phone: ''
    });

    useEffect(() => {
        const loadBookedDates = async () => {
            try {
                const dates = await fetchBookedDates();
                // Convert string dates to Date objects
                const formattedDates = dates.map(dateStr => new Date(dateStr));
                setBookedDates(formattedDates);
            } catch (error) {
                console.error('Error loading booked dates:', error);
                // Fallback to example dates if API fails
                const today = new Date();
                const date1 = new Date(today);
                date1.setDate(today.getDate() + 7);
                const date2 = new Date(today);
                date2.setDate(today.getDate() + 14);
                setBookedDates([date1, date2]);
            }
        };

        const loadUserInfo = () => {
            try {
                const userString = sessionStorage.getItem('user');
                if (userString) {
                    const user = JSON.parse(userString);
                    setCustomerInfo(prevInfo => ({
                        ...prevInfo,
                        name: user.name || '',
                        email: user.email || ''
                    }));
                }
            } catch (error) {
                console.error('Error loading user info:', error);
            }
        };

        loadBookedDates();
        loadUserInfo();
    }, []);

    const calculateTotal = () => {
        // Calculate total from the event package price
        const eventPrice = Number(selectedEventPackage.price) || 0;
        
        // Calculate decoration total from selected decorations
        const decorationTotal = selectedDecorations.reduce((sum, deco) => sum + (Number(deco.price) || 0), 0);
        
        // Return total of event price + decoration prices
        return eventPrice + decorationTotal;
    };

    const formatDateTimeDisplay = () => {
        if (!selectedDate || !startTime || !endTime) return null;
        return `${selectedDate.toDateString()} (${startTime} - ${endTime})`;
    };

    const handleNextStep = () => {
        setCurrentStep(prevStep => Math.min(prevStep + 1, 4));
    };

    const handlePrevStep = () => {
        setCurrentStep(prevStep => Math.max(prevStep - 1, 1));
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <MainFunction
                        selectedOccasion={selectedEventPackage.name}
                        onSelectOccasion={setSelectedEventPackage}
                    />
                );
            case 2:
                return (
                    <DateTimeSelector
                        selectedDate={selectedDate}
                        startTime={startTime}
                        endTime={endTime}
                        onDateChange={setSelectedDate}
                        onStartTimeChange={setStartTime}
                        onEndTimeChange={setEndTime}
                        bookedDates={bookedDates}
                    />
                );
            case 3:
                return (
                    <DecorationServices
                        selectedDecorations={selectedDecorations}
                        onSelectDecoration={setSelectedDecorations}
                        functionId={selectedEventPackage.id}
                    />
                );
            case 4:
                return (
                    <div className="booking-step-3-container">
                        <div className="equipment-section">
                            {/* Pass the functionId prop to EquipmentRental */}
                            <EquipmentRental functionId={selectedEventPackage.id} />
                        </div>
                        <div className="summary-section-container">
                            <BookingSummary
                                selectedEventPackage={selectedEventPackage.name}
                                occasionPrice={selectedEventPackage.price}
                                selectedDecorations={selectedDecorations}
                                decorationPrice={selectedDecorations.reduce((sum, d) => sum + d.price, 0)}
                                selectedDateTime={formatDateTimeDisplay()}
                                selectedDate={selectedDate}
                                startTime={startTime}
                                endTime={endTime}
                                baseTotal={calculateTotal()}
                                customerInfo={customerInfo}
                                eventPackageId={selectedEventPackage.id} // Pass the selected event package ID
                            />
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="booking-container">
            <h2 className="booking-title text-center mb-4">Book Your Event</h2>

            <div className="progress-steps mb-4">
                <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
                    <div className="step-number">1</div>
                    <div className="step-label">Select Event Package</div>
                </div>
                <div className={`step-connector ${currentStep >= 2 ? 'active' : ''}`}></div>
                <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
                    <div className="step-number">2</div>
                    <div className="step-label">Select Date & Time</div>
                </div>
                <div className={`step-connector ${currentStep >= 3 ? 'active' : ''}`}></div>
                <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
                    <div className="step-number">3</div>
                    <div className="step-label">Select Decoration Services</div>
                </div>
                <div className={`step-connector ${currentStep >= 4 ? 'active' : ''}`}></div>
                <div className={`step ${currentStep >= 4 ? 'active' : ''}`}>
                    <div className="step-number">4</div>
                    <div className="step-label">Equipment & Confirm</div>
                </div>
            </div>

            <div className="booking-content">
                {renderStepContent()}
            </div>

            <div className="booking-navigation mt-4">
                {currentStep > 1 && (
                    <button
                        className="btn btn-outline-primary"
                        onClick={handlePrevStep}
                        disabled={isSubmitting}
                    >
                        Previous
                    </button>
                )}

                {currentStep < 4 && (
                    <button
                        className="btn btn-primary ms-2"
                        onClick={handleNextStep}
                        disabled={
                            (currentStep === 1 && !selectedEventPackage.name) ||
                            (currentStep === 2 && (!selectedDate || !startTime || !endTime)) ||
                            (currentStep === 3 && selectedDecorations.length === 0)
                        }
                    >
                        Next
                    </button>
                )}
            </div>
        </div>
    );
};

export default BookingSystem;